import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Sparkles, Lock } from 'lucide-react';
import { adminLogin, adminMe, formatApiError } from '../../services/api';
import { Fireflies } from '../../components/FairyElements';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in, go to dashboard
    adminMe().then(() => navigate('/admin/dashboard')).catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminLogin(email.trim(), password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 forest-bg"
      style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.45 }}
    >
      <Fireflies count={40} />

      <Card className="forest-card border-0 w-full max-w-md relative z-10" data-testid="admin-login-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-emerald-700 to-amber-600 rounded-full flex items-center justify-center mystical-glow-gold border border-amber-500/40">
            <Lock className="w-8 h-8 text-amber-200" />
          </div>
          <CardTitle className="text-3xl gradient-text" style={{ fontFamily: 'Cinzel, serif' }}>
            Admin Portal
          </CardTitle>
          <p className="text-amber-100/70 font-serif-mystical italic mt-2">
            The mystical garden's sacred entry
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-amber-300 uppercase tracking-wider text-xs" style={{ fontFamily: 'Cinzel, serif' }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="meeta@thedivine7777.com"
                required
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100 placeholder:text-amber-100/40 focus:border-amber-500"
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-amber-300 uppercase tracking-wider text-xs" style={{ fontFamily: 'Cinzel, serif' }}>
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your secret password"
                required
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100 placeholder:text-amber-100/40 focus:border-amber-500"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div className="p-3 rounded-md bg-red-950/60 border border-red-500/40 text-red-300 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="btn-gold w-full py-6 text-base"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {loading ? 'Entering...' : 'Enter the Garden'}
            </Button>
          </form>

          <p className="text-center text-xs text-amber-100/50 mt-6 font-serif-mystical italic">
            For The Divine7777 administration only
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
